import Link from 'next/link'
import { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import Avatar from '@/components/billboard/top100/content/avatar'
import Top1Icon from '@/components/icon/billboard/top100/top1.svg'
import Top2Icon from '@/components/icon/billboard/top100/top2.svg'
import Top3Icon from '@/components/icon/billboard/top100/top3.svg'
import routes from '@/hooks/use-authorization/routes'
import { useI18n } from '@/hooks/use-i18n'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  AnalyzeRankingItem,
  RankingPlatformType,
} from '@/types/schema/k6/analyze-api'
import { isPlatformShortcode } from '@/utils/platform/is-platform-shortcode'

interface CardProps {
  className?: string
  platform: RankingPlatformType
  analyzeRankingItem: AnalyzeRankingItem
}

const Card: FunctionComponent<CardProps> = ({
  className,
  platform,
  analyzeRankingItem,
}) => {
  const {
    uuid,
    name,
    followerCount,
    rank,
    wordOfMouthVolume,
    engagementRate,
    viewRate,
  } = analyzeRankingItem
  const { formatNumber } = useIntl()
  const { locale } = useI18n()

  const from = 'Landing Page'

  const upperCasePlatform = platform.toUpperCase()

  return (
    <Wrapper className={className}>
      <Badge>
        {((): ReactNode => {
          if (rank === 1) {
            return <Top1Icon />
          }
          if (rank === 2) {
            return <Top2Icon />
          }
          if (rank === 3) {
            return <Top3Icon />
          }
          return null
        })()}
      </Badge>
      <Avatar
        from='Landing Page'
        height={80}
        platform={platform}
        uuid={uuid}
        width={80}
      />
      <Name>
        <Link
          href={routes.publicKolDetail.stringifyUrl(
            { kolId: uuid },
            {
              from,
              lang: locale,
              platform: isPlatformShortcode(platform) ? platform : undefined,
            },
          )}
        >
          {name}
        </Link>
      </Name>
      <Description>
        {followerCount === null ? '-' : formatNumber(followerCount)}{' '}
        {upperCasePlatform} 追蹤數
      </Description>
      <InfoContainer>
        <Info>
          <InfoTitle>{upperCasePlatform} 口碑聲量</InfoTitle>
          <InfoValue>
            {wordOfMouthVolume === null
              ? '-'
              : formatNumber(wordOfMouthVolume, { isCompactDisplay: true })}
          </InfoValue>
        </Info>
        <Info>
          <InfoTitle>{upperCasePlatform} 互動率</InfoTitle>
          <InfoValue>
            {engagementRate === null
              ? '-'
              : `${Math.ceil(engagementRate * 100)}%`}
          </InfoValue>
        </Info>
        <Info>
          <InfoTitle>{upperCasePlatform} 觀看率</InfoTitle>
          <InfoValue>
            {viewRate === null ? '-' : `${Math.ceil(viewRate * 100)}%`}
          </InfoValue>
        </Info>
      </InfoContainer>
    </Wrapper>
  )
}

const Badge = styled.div`
  position: absolute;
  top: 0;
  left: 24px;
  width: 80px;

  svg {
    color: ${({ theme }): string => theme.colors.brand.primary};
  }
`

const Name = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 700;

  a {
    &,
    &:hover {
      color: ${({ theme }): string => theme.colors.text.primary};
    }
  }
`

const Description = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 16px;
`

const InfoTitle = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 14px;
`

const InfoValue = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 20px;
  font-weight: 700;
`

const Info = styled.div`
  padding: 4px 8px;

  ${InfoTitle} {
    margin-bottom: 8px;
  }
`

const InfoContainer = styled.div`
  display: flex;
  text-align: center;
  justify-content: center;

  ${Info}:nth-child(2) {
    border-left: 1px solid
      ${({ theme }): string => theme.colors.base.grey.grey6};
    border-right: 1px solid
      ${({ theme }): string => theme.colors.base.grey.grey6};
  }
`

const Wrapper = styled.div`
  position: relative;
  padding: 24px 16px;
  background-color: ${({ theme }): string => theme.colors.background.white};
  line-height: 1.4;
  text-align: center;

  ${Avatar} {
    margin: 0 auto 28px;
  }

  ${Name} {
    margin-bottom: 8px;
  }

  ${Description} {
    margin-bottom: 36px;
  }
`

export default styled(Card)``
